import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { HeroScene } from "@/components/three/HeroScene";
import { LineReveal, EASE } from "@/components/site/motion";
import { PillLink } from "@/components/site/Buttons";
import { scrollToTarget } from "@/components/site/SmoothScroll";

const HeroHud = ({ ready }) => {
  const [hdg, setHdg] = useState(72.4);
  useEffect(() => {
    const id = setInterval(() => setHdg(72.4 + Math.sin(Date.now() / 3800) * 0.7), 900);
    return () => clearInterval(id);
  }, []);
  const items = [
    ["Crew onboard", "00"],
    ["Fleet link", "Connected"],
    ["Supervision", "ROC · ashore"],
    ["Heading", `${hdg.toFixed(1)}°`],
  ];
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: ready ? 1 : 0 }}
      transition={{ delay: 1.1, duration: 1 }}
      className="mt-10 hidden grid-cols-4 gap-x-6 border-t border-white/10 pt-4 lg:grid"
      data-testid="hero-hud"
    >
      {items.map(([k, v]) => (
        <div key={k} className="flex flex-col gap-1 xl:flex-row xl:items-baseline xl:gap-3">
          <span className="eyebrow text-fg-3">{k}</span>
          <span className="mono text-[13px] text-fg tabular-nums">{v}</span>
        </div>
      ))}
    </motion.div>
  );
};

export const Hero = ({ ready }) => (
  <section className="relative h-[100svh] min-h-[660px] w-full overflow-hidden" data-testid="hero-section" data-analytics-section="hero">
    <HeroScene play={ready} className="absolute inset-0" />
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink via-ink/25 to-transparent" />
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/25 to-transparent md:w-3/4" />
    <div className="shell relative z-10 flex h-full flex-col justify-end pb-6 sm:pb-10">
      <LineReveal
        as="h1"
        play={ready}
        delay={0.12}
        lines={["Autonomous", "cargo ships."]}
        className="text-[12.4vw] font-[560] leading-[0.92] tracking-[-0.05em] text-fg sm:text-7xl lg:text-[6.6rem] xl:text-[8.2rem]"
        testId="hero-title"
      />
      <div className="mt-7 grid items-end gap-6 sm:mt-8 sm:gap-7 lg:grid-cols-12">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: ready ? 1 : 0, y: ready ? 0 : 16 }}
          transition={{ delay: 0.7, duration: 0.9, ease: EASE }}
          className="max-w-xl text-base leading-relaxed text-fg-2 sm:text-lg lg:col-span-5"
          data-testid="hero-subtitle"
        >
          We’re developing completely unmanned ships that operate as a connected fleet, with people ashore supervising operations, handling communications and resolving exceptions.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: ready ? 1 : 0, y: ready ? 0 : 16 }}
          transition={{ delay: 0.85, duration: 0.9, ease: EASE }}
          className="flex flex-wrap gap-3 lg:col-span-7 lg:justify-end"
        >
          <PillLink to="/careers" testId="hero-join-cta">Join us</PillLink>
          <PillLink to="/partners" variant="ghost" testId="hero-partner-cta">Partner with us</PillLink>
          <PillLink variant="ghost" onClick={() => scrollToTarget("#subscribe")} testId="hero-subscribe-cta" data-analytics-event="cta_subscribe">Stay updated</PillLink>
        </motion.div>
      </div>
      <HeroHud ready={ready} />
    </div>
  </section>
);
